import { Card, CardContent, CardFooter } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

interface PostMemeCardSkeletonProps {
  showImage?: boolean
  textLines?: number
  showTags?: boolean
}

export default function PostMemeCardSkeleton({
  showImage = true,
  textLines = 3,
  showTags = true,
}: PostMemeCardSkeletonProps) {
  return (
    <Card className="w-full bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden animate-pulse">
      <CardContent className="p-0">
        <div className="p-3 sm:p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3 mb-3">
              <Skeleton className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gray-200" />
              <div className="flex-1">
                <Skeleton className="h-3 sm:h-4 w-24 sm:w-32 mb-1 sm:mb-2 bg-gray-200" />
                <Skeleton className="h-2 sm:h-3 w-16 sm:w-24 bg-gray-200" />
              </div>
            </div>
            <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded-full bg-gray-200" />
          </div>
        </div>

        <div className="p-3 sm:p-4">
          <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
            {Array.from({ length: textLines }).map((_, index) => (
              <Skeleton
                key={index}
                className={`h-3 sm:h-4 bg-gray-200 ${
                  index === 0 ? 'w-full' : index === 1 ? 'w-4/5' : index === 2 ? 'w-3/5' : 'w-2/3'
                }`}
              />
            ))}
          </div>

          {showTags && (
            <div className="flex gap-1 sm:gap-2 mb-3 sm:mb-4 flex-wrap">
              <Skeleton className="h-5 sm:h-6 w-12 sm:w-16 rounded-full bg-gray-200" />
              <Skeleton className="h-5 sm:h-6 w-16 sm:w-20 rounded-full bg-gray-200" />
              <Skeleton className="h-5 sm:h-6 w-10 sm:w-14 rounded-full bg-gray-200" />
            </div>
          )}

          {showImage && <Skeleton className="h-48 sm:h-64 w-full rounded-lg mb-3 sm:mb-4 bg-gray-200" />}
        </div>
      </CardContent>

      <CardFooter className="flex flex-col gap-1 sm:gap-2 w-full p-0">
        <div className="px-3 sm:px-4 py-2 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 sm:gap-2">
              <div className="flex -space-x-1">
                <Skeleton className="h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-gray-200" />
                <Skeleton className="h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-gray-200" />
                <Skeleton className="h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-gray-200" />
              </div>
              <Skeleton className="h-3 sm:h-4 w-12 sm:w-16 bg-gray-200" />
            </div>
            <Skeleton className="h-3 sm:h-4 w-16 sm:w-20 bg-gray-200" />
          </div>
        </div>

        <div className="px-3 sm:px-4 pb-3 sm:pb-4 w-full">
          <div className="flex pt-2 border-t border-gray-200 gap-1 sm:gap-2">
            <Skeleton className="h-8 sm:h-10 flex-1 rounded-md bg-gray-200" />
            <Skeleton className="h-8 sm:h-10 flex-1 rounded-md bg-gray-200" />
            <Skeleton className="h-8 sm:h-10 flex-1 rounded-md bg-gray-200" />
          </div>
        </div>
      </CardFooter>
    </Card>
  )
}

export function PostMemeCardSkeletonList({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, index) => (
        <PostMemeCardSkeleton
          key={index}
          showImage={Math.random() > 0.3}
          textLines={Math.floor(Math.random() * 3) + 2}
          showTags={Math.random() > 0.4}
        />
      ))}
    </div>
  )
}
